import React, { FC, useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Badge, Tab, Tabs, styled, useTheme } from "@mui/material";

import {
	countStatusForSuggestion,
	countSuggestionsByUser,
} from "@/components/Application/SideBarPro";
import { Translate } from "@/components/Application/Translate";
import { tokens } from "@/components/Application/theme";

import { UserContext } from "@/model/User/UserContext";

import { useFetchProcedureUpdateSuggestions } from "@/service/TaskQueries";

export const ProceduresPage: FC = () => {
	const navigateTo = useNavigate();
	const [selectedMainTab, setSelectedMainTab] = useState(0);
	const [selectedSecondaryTab, setSelectedSecondaryTab] = useState(0);
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const location = useLocation();

	const user = useContext(UserContext);
	const isProcedureSuggestionsAdministrator =
		user?.IsProcedureSuggestionsAdministrator;
	const { data: suggestionsData } = useFetchProcedureUpdateSuggestions(false);

	const suggestionsToVerifyUnverified = suggestionsData
		? countStatusForSuggestion(suggestionsData, "Unverified")
		: 0;

	const suggestionsToVerifyUser = suggestionsData
		? countSuggestionsByUser(suggestionsData, user?.Id)
		: 0;

	useEffect(() => {
		const path = location.pathname;

		if (path.includes("/procedures/procedureUpdates/")) {
			setSelectedMainTab(1); // Procedure Updates tab
			if (isProcedureSuggestionsAdministrator) {
				if (path.includes("/newImprov")) setSelectedSecondaryTab(0);
				else if (path.includes("/myImprov")) setSelectedSecondaryTab(1);
				else if (path.includes("/verified")) setSelectedSecondaryTab(2);
				else if (path.includes("/declined")) setSelectedSecondaryTab(3);
			} else {
				if (path.includes("/myImprov")) setSelectedSecondaryTab(0);
				else if (path.includes("/verified")) setSelectedSecondaryTab(1);
				else if (path.includes("/declined")) setSelectedSecondaryTab(2);
			}
		} else if (path.includes("/procedures/compareProcedures/")) {
			setSelectedMainTab(2); // Compare Procedures tab
		} else if (path.includes("/procedures") || path.includes("/procedure/")) {
			setSelectedMainTab(0); // Default to Procedures tab
		}
	}, [location.pathname, isProcedureSuggestionsAdministrator]);

	const StyledBadge = styled(Badge)(({ theme, color }) => ({
		"& .MuiBadge-badge": {
			border: `1px solid ${theme.palette[color].main}`,
			padding: "0 4px",
			color: "black",
			backgroundColor: "transparent",
		},
	}));

	const StyledTab = styled((props) => (
		<Tab
			{...props}
			label={
				<div style={{ display: "flex", alignItems: "center" }}>
					{props.label}
					{props.badge && props.color && (
						<StyledBadge
							variant="standard"
							badgeContent={props.badge ? props.badge : ""}
							color={props.color}
							style={{ marginLeft: 8, marginTop: "-18px" }}
						/>
					)}
				</div>
			}
		/>
	))({
		"&.Mui-selected": {
			color: colors.primary[100],
		},
		textTransform: "none",
	});

	const handleMainTabChange = (event, newValue) => {
		setSelectedMainTab(newValue);
		setSelectedSecondaryTab(0); // Reset secondary tab when changing main tab
		switch (newValue) {
			case 0:
				navigateTo("/procedures/");
				break;
			case 1:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/procedures/procedureUpdates/newImprov");
				} else {
					navigateTo("/procedures/procedureUpdates/myImprov");
				}
				break;
			case 2:
				navigateTo("/procedures/compareProcedures/");
				break;
			default:
				break;
		}
	};

	const handleSecondaryTabChangeProcedure = (event, newValue) => {
		setSelectedSecondaryTab(newValue);
		switch (newValue) {
			case 0:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/procedures/procedureUpdates/newImprov");
				} else {
					navigateTo("/procedures/procedureUpdates/myImprov");
				}
				break;
			case 1:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/procedures/procedureUpdates/myImprov");
				} else {
					navigateTo("/procedures/procedureUpdates/verified");
				}
				break;
			case 2:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/procedures/procedureUpdates/verified");
				} else {
					navigateTo("/procedures/procedureUpdates/declined");
				}
				break;
			case 3:
				navigateTo("/procedures/procedureUpdates/declined");
				break;
			default:
				break;
		}
	};

	return (
		<div>
			<Tabs
				value={selectedMainTab}
				onChange={handleMainTabChange}
				TabIndicatorProps={{
					style: {
						backgroundColor: "#2555d4",
					},
				}}
				TabScrollButtonProps={{
					style: {
						backgroundColor: "#2555d4",
					},
				}}
			>
				<StyledTab label={<Translate String="Breadcrumbs.Link.Procedures" />} />
				<StyledTab
					label={<Translate String="Task.Procedure.Updates" />}
					{...(suggestionsToVerifyUnverified && {
						badge: isProcedureSuggestionsAdministrator
							? suggestionsToVerifyUnverified
							: suggestionsToVerifyUser,
					})}
					color="error"
				/>
				<StyledTab label={<Translate String="Task.Compare.Procedures" />} />
			</Tabs>
			{selectedMainTab === 1 && (
				<Tabs
					value={selectedSecondaryTab}
					onChange={handleSecondaryTabChangeProcedure}
					TabIndicatorProps={{
						style: {
							backgroundColor: "#2555d4",
						},
					}}
					TabScrollButtonProps={{
						style: {
							backgroundColor: "#2555d4",
						},
					}}
					sx={{ marginTop: "10px" }}
				>
					{isProcedureSuggestionsAdministrator && (
						<StyledTab
							label={<Translate String="Task.Improvement.New.Improvements" />}
						/>
					)}
					<StyledTab
						label={<Translate String="Task.Improvement.My.Improvements" />}
						color="error"
					/>
					<StyledTab
						label={
							<Translate String="Task.Improvement.Verified.Improvements" />
						}
						color="success"
					/>
					<StyledTab
						label={
							<Translate String="Task.Improvement.Declined.Improvements" />
						}
					/>
				</Tabs>
			)}
			<Outlet />
		</div>
	);
};

export default ProceduresPage;
